import { ReactElement, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import {
  TensileTransmitterList,
  StructuralPlanningElementFormHeader,
} from '@structuralPlanningComponents'
import { AxiosError } from 'axios'
import { find } from 'lodash-es'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Stack, Tab, Box, styled } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlignmentButton, DeleteButton } from '@ui/actions'
import { useSnackbar } from '@ui/snackbar'
import { useModelStore, useTransmissionStore } from '@editorStores'
import { useResultsInvalidation, useElementType, useElementLabel } from '@editorHooks'
import {
  CollapsibleActionsBoxSection,
  CollapsibleAssemblyBoxSection,
  CollapsibleCopyBoxSection,
  CollapsibleDocumentationBoxSection,
  CollapsibleGeometryBoxSection,
  CollapsibleProblemsBoxSection,
  CollapsibleSettingsBoxSection,
  DocumentationItem,
  SingleElementCSForm,
} from '@editorComponents'
import { useModelInvalidation, useStructuralPlanningSelection } from '@structuralPlanningHooks'
import { getModel, getProjectFiles } from '@queries'
import { deleteRip, recalculateRipShape, recalculateTensileTargets } from '@mutations'
import { buildErrorMessage, documentationRootGuid } from 'src/constants'
import { useSaveTensileTransmissionGraph } from '../../hooks/useSaveTensileTransmissionGraph'
import PlanSettingsForm from '../PlanSettingsForm'
import VerticalTransmitterList from '../VerticalTransmitterList'
import RipCopyForm from './components/RipCopyForm'
import RipGeometryForm from './components/RipGeometryForm'
import { positionTabs } from './tabs'

const StyledTabPanel = styled(TabPanel)({
  paddingLeft: 8,
  paddingRight: 8,
  paddingTop: 8,
  paddingBottom: 0,
})

interface Props {
  position: string
  onClose?: () => void
}

const PositionForm = ({ position, onClose }: Props): ReactElement => {
  const { projectId } = useParams()
  const client = useQueryClient()
  const invalidateResults = useResultsInvalidation()
  const invalidateModel = useModelInvalidation()
  const { enqueueSnackbar } = useSnackbar()

  const {
    params: { verticalTransmitter, selectedElements, activeTab },
    actions: { setVerticalTransmitter, setActiveTab },
  } = useStructuralPlanningSelection()

  const setTensileTransmissionGraph = useTransmissionStore(
    state => state.setTensileTransmissionGraph,
  )

  const elementType = useElementType(position)
  const rips = useModelStore(state => state.model.rips)
  const rip = useMemo(() => find(rips, { position_guid: position }), [rips, position])
  const lintels = useModelStore(state => state.model.lintels)
  const lintel = useMemo(() => find(lintels, { position_guid: position }), [lintels, position])
  const wallPosition = rip ?? lintel
  const showRipGeometryForm = elementType === 'rips' && !!rip
  const isOpeningRip = rip?.rip_type === 'OpeningRip'

  const getLabel = useElementLabel()

  const label = getLabel(position)

  const { mutate: handleDelete, isLoading: isDeleting } = useMutation(
    (position: string) => {
      return deleteRip.request(projectId, position)
    },
    {
      onSuccess: async () => {
        await Promise.all([
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getProjectFiles.getKey(projectId, documentationRootGuid)),
        ])
        invalidateResults(projectId as string)
        enqueueSnackbar('Wandrippe erfolgreich gelöscht', { variant: 'success' })
      },
      onError: (error: Error | AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Löschen der Wandrippe'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutate: handleRecalculateRipShape, isLoading: isRecalculatingRipShape } = useMutation(
    () => recalculateRipShape.request(projectId as string, position),
    {
      onSuccess: async () => {
        await invalidateModel(projectId as string)
        invalidateResults(projectId as string)
        enqueueSnackbar('Rippengeometrie erfolgreich neu berechnet', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Neuberechnen der Rippengeometrie'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutate: saveGraph, isLoading: isSavingGraph } = useSaveTensileTransmissionGraph(
    projectId as string,
  )

  const { mutate: recalculateTargets, isLoading: isRecalculating } = useMutation(
    () => recalculateTensileTargets.request(projectId, selectedElements?.[0] as string),
    {
      onSuccess: async data => {
        setTensileTransmissionGraph(data)
        invalidateResults(projectId as string)
        enqueueSnackbar('Ziele erfolgreich neu berechnet', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim der Berechnung der Ziele'), {
          variant: 'error',
        })
      },
    },
  )

  const selectedTab = useMemo(() => {
    if (activeTab && find(Object.entries(positionTabs), ([, e]) => e.tab === activeTab))
      return activeTab

    return positionTabs.geometry.tab
  }, [activeTab])

  return (
    <Box>
      <TabContext value={selectedTab}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              fontSize: 12,
              paddingX: ({ spacing }) => spacing(1.5),
            },
          }}
        >
          <TabList
            onChange={(_, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              value={positionTabs.geometry.tab}
              label={positionTabs.geometry.label}
              data-cy="tab-geometry"
            />
            <Tab
              value={positionTabs.verticalTransmission.tab}
              label={positionTabs.verticalTransmission.label}
              data-cy="tab-vertical-transmission"
            />
            <Tab
              value={positionTabs.connector.tab}
              label={positionTabs.connector.label}
              data-cy="tab-connector"
            />
          </TabList>
        </Box>
        <StyledTabPanel value={positionTabs.connector.tab}>
          <TensileTransmitterList
            selectedElement={position}
            onSave={saveGraph}
            onRecalculate={() => recalculateTargets()}
            isRecalculating={isRecalculating}
            isLoading={isSavingGraph}
          />
        </StyledTabPanel>
        <StyledTabPanel value={positionTabs.verticalTransmission.tab}>
          <VerticalTransmitterList
            selectedElement={position}
            verticalTransmitter={verticalTransmitter}
            setVerticalTransmitter={setVerticalTransmitter}
          />
        </StyledTabPanel>
        <StyledTabPanel value={positionTabs.geometry.tab}>
          <Stack direction="column" spacing={1}>
            <StructuralPlanningElementFormHeader
              elementGuid={position}
              label={label}
              onClose={onClose}
            />

            {showRipGeometryForm && (
              <CollapsibleGeometryBoxSection defaultExpanded={true}>
                <Stack direction="column" spacing={1}>
                  <RipGeometryForm positionGuid={position} readOnly={isOpeningRip} />
                  {!isOpeningRip && (
                    <DeleteButton
                      onClick={() => handleDelete(position)}
                      loading={isDeleting}
                      data-cy="delete-rip-btn"
                      fullWidth
                      size="small"
                    >
                      Löschen
                    </DeleteButton>
                  )}
                </Stack>
              </CollapsibleGeometryBoxSection>
            )}

            <CollapsibleAssemblyBoxSection label="Querschnitt" defaultExpanded={true}>
              <SingleElementCSForm
                selectedElement={position}
                elementType={elementType as ElementTypes}
              />
            </CollapsibleAssemblyBoxSection>

            {wallPosition && !wallPosition.is_local && (
              <CollapsibleSettingsBoxSection defaultExpanded={false}>
                <PlanSettingsForm element={wallPosition} />
              </CollapsibleSettingsBoxSection>
            )}

            {elementType === 'rips' && rip && (
              <CollapsibleCopyBoxSection label="kopieren" defaultExpanded={false}>
                <RipCopyForm rips={[rip]} />
              </CollapsibleCopyBoxSection>
            )}

            <CollapsibleDocumentationBoxSection defaultExpanded={false}>
              <DocumentationItem itemGuid={position} />
            </CollapsibleDocumentationBoxSection>

            <CollapsibleProblemsBoxSection elementGuid={position} defaultExpanded={true} />

            {elementType === 'rips' && rip && !rip.is_local && (
              <CollapsibleActionsBoxSection label="Aktionen" defaultExpanded={true}>
                <Stack direction="row" spacing={1} justifyContent="space-evenly">
                  <AlignmentButton
                    loading={isRecalculatingRipShape}
                    onClick={() => handleRecalculateRipShape()}
                    data-cy="recalculate-rip-shape-btn"
                    size="small"
                  >
                    Geometrie neu berechnen
                  </AlignmentButton>
                </Stack>
              </CollapsibleActionsBoxSection>
            )}
          </Stack>
        </StyledTabPanel>
      </TabContext>
    </Box>
  )
}

export default PositionForm
